import { FormInstance } from 'antd';

import { Filter } from './filters.type';
import { getEnumKeyByEnumValue } from './helpers';
import {
  ContractTypeEnum,
  PriorityEnum,
  ReminderObjectClassEnum,
  ReminderTypeEnum,
  RemotePreferenceEnum,
} from './types';

export type SearchFormProps = {
  form: FormInstance;
  filter: Filter;
};

export enum SearchFormFieldEnum {
  Active = 'active',
  All = 'ALL',
  AppliedPosition = 'appliedPosition',
  Backoffices = 'backoffices.id',
  BackofficesFirstName = 'backoffices.firstName',
  BackofficesLastName = 'backoffices.lastName',
  Body = 'body',
  CandidateFirstName = 'candidate.firstName',
  CandidateLastName = 'candidate.lastName',
  CandidateRecommendedBy = 'candidate.recommendedBy',
  CandidateUUID = 'candidate.uuid',
  City = 'city',
  ClientEmployeeFirstName = 'clientEmployee.firstName',
  ClientEmployeeLastName = 'clientEmployee.lastName',
  ClientName = 'client.name',
  ContractTypes = 'contractTypes',
  Coordinators = 'coordinators.id',
  CoordinatorsFirstName = 'coordinators.firstName',
  CoordinatorsLastName = 'coordinators.lastName',
  CreatedDate = 'createdDate',
  CV = 'cv',
  ExternalId = 'externalId',
  FirstName = 'firstName',
  From = 'from',
  HrInterviewAssessment = 'hrInterviewAssessment',
  HrInterviewDate = 'hrInterviewDate',
  HrInterviewSummary = 'hrInterviewSummary',
  Interviewers = 'interviewers.id',
  LastModifiedDate = 'lastModifiedDate',
  LastName = 'lastName',
  Name = 'name',
  Priority = 'priority',
  ProjectActive = 'project.active',
  ProjectBackOfficesId = 'project.backoffices.id',
  ProjectBackOfficesFirstName = 'project.backoffices.firstName',
  ProjectBackOfficesLastName = 'project.backoffices.lastName',
  ProjectClientName = 'project.client.name',
  ProjectClientId = 'project.client.uuid',
  ProjectCoordinatorsId = 'project.coordinators.id',
  ProjectCoordinatorsFirstName = 'project.coordinators.firstName',
  ProjectCoordinatorsLastName = 'project.coordinators.lastName',
  ProjectInterviewersId = 'project.interviewers.id',
  ProjectInterviewersFirstName = 'project.interviewers.firstName',
  ProjectInterviewersLastName = 'project.interviewers.lastName',
  ProjectName = 'project.name',
  ProjectPriority = 'project.priority',
  ProjectRecruitersId = 'project.recruiters.id',
  ProjectRecruitersFirstName = 'project.recruiters.firstName',
  ProjectRecruitersLastName = 'project.recruiters.lastName',
  ProjectUUID = 'project.uuid',
  RecommendedBy = 'recommendedBy',
  Recruiters = 'recruiters.id',
  RemindAtDate = 'date',
  ReminderFor = 'objectClass',
  ReminderType = 'type',
  RemotePreference = 'remotePreference',
  ResponsibleId = 'responsible.id',
  ResponsibleFirstName = 'responsible.firstName',
  ResponsibleLastName = 'responsible.lastName',
  SentDate = 'sentDate',
  Source = 'source',
  StatusActionRequired = 'status.actionRequired',
  Subject = 'subject',
  TechnicalInterviewAssessment = 'technicalInterviewAssessment',
  TechnicalInterviewDate = 'technicalInterviewDate',
  TechnicalInterviewSummary = 'technicalInterviewSummary',
  TotalEmploymentCost = 'totalEmploymentCost',
  Title = 'title',
  UserFirstName = 'user.firstName',
  UserLastName = 'user.lastName',
  UUID = 'uuid',
  PerfectMatch = 'perfectMatch'
}

export enum SearchFormInputEnum {
  all = 'ALL',
  formCheckbox = 'formCheckbox',
  formCheckboxGroup = 'formCheckboxGroup',
  formDatePicker = 'formDatePicker',
  formDatePickerStart = 'formDatePickerStart',
  formDatePickerEnd = 'formDatePickerEnd',
  formText = 'formText',
  formTextWithOperator = 'formTextWithOperator',
  formDatePickerWithOperator = 'formDatePickerWithOperator',
  formSelect = 'formSelect',
  formMultipleSelect = 'formMultipleSelect',
  formMixedMutipleSelectWithRadioGroup = 'formMixedMutipleSelectWithRadioGroup',
}

type SearchFormFieldsDataType = {
  searchFormFieldType: SearchFormInputEnum;
  sortable: boolean;
  label: string;
  toFormValueFormatter?: (value: string[]) => string[];
  toPayloadValueFormatter?: (value: string[]) => string[];
};

export const SearchFormFieldsData: Record<SearchFormFieldEnum, SearchFormFieldsDataType> = {
  [SearchFormFieldEnum.Active]: {
    searchFormFieldType: SearchFormInputEnum.formCheckbox,
    sortable: true,
    label: 'Active',
  },
  [SearchFormFieldEnum.All]: {
    searchFormFieldType: SearchFormInputEnum.all,
    sortable: true,
    label: 'All',
  },
  [SearchFormFieldEnum.AppliedPosition]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Applied position',
  },
  [SearchFormFieldEnum.Backoffices]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Backoffice',
  },
  [SearchFormFieldEnum.BackofficesFirstName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Backoffices first name',
  },
  [SearchFormFieldEnum.BackofficesLastName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Backoffices last name',
  },
  [SearchFormFieldEnum.Body]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Body',
  },
  [SearchFormFieldEnum.CandidateFirstName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'First name',
  },
  [SearchFormFieldEnum.CandidateLastName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Last name',
  },
  [SearchFormFieldEnum.CandidateRecommendedBy]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Recommended by',
  },
  [SearchFormFieldEnum.CandidateUUID]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Candidate',
  },
  [SearchFormFieldEnum.City]: {
    searchFormFieldType: SearchFormInputEnum.formMultipleSelect,
    sortable: true,
    label: 'City',
    toPayloadValueFormatter: (value: string[]): string[] => value,
    toFormValueFormatter: (value: string[]): string[] => value,
  },
  [SearchFormFieldEnum.ClientEmployeeFirstName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Client employee first name',
  },
  [SearchFormFieldEnum.ClientEmployeeLastName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Client employee last name',
  },
  [SearchFormFieldEnum.ClientName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Client name',
  },
  [SearchFormFieldEnum.ContractTypes]: {
    searchFormFieldType: SearchFormInputEnum.formCheckboxGroup,
    sortable: false,
    label: 'Supported contract',
    toPayloadValueFormatter: (value: string[]): string[] => {
      return value?.length ? value.flatMap((element) => getEnumKeyByEnumValue(ContractTypeEnum, element) || []) : [];
    },
    toFormValueFormatter: (value: string[]) =>
      value.map((element) => ContractTypeEnum[element as keyof typeof ContractTypeEnum]),
  },
  [SearchFormFieldEnum.Coordinators]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Coordinators',
  },
  [SearchFormFieldEnum.CoordinatorsFirstName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Coordinator first name',
  },
  [SearchFormFieldEnum.CoordinatorsLastName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Coordinator last name',
  },
  [SearchFormFieldEnum.CreatedDate]: {
    searchFormFieldType: SearchFormInputEnum.formDatePicker,
    sortable: true,
    label: 'Created',
  },
  [SearchFormFieldEnum.CV]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: false,
    label: 'CV',
  },
  [SearchFormFieldEnum.ExternalId]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'External ID',
  },
  [SearchFormFieldEnum.HrInterviewAssessment]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: false,
    label: 'HR score / assessment',
  },
  [SearchFormFieldEnum.HrInterviewDate]: {
    searchFormFieldType: SearchFormInputEnum.formDatePicker,
    sortable: false,
    label: 'HR interview date',
  },
  [SearchFormFieldEnum.HrInterviewSummary]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: false,
    label: 'HR interview description',
  },
  [SearchFormFieldEnum.Interviewers]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Interviewers',
  },
  [SearchFormFieldEnum.RemindAtDate]: {
    searchFormFieldType: SearchFormInputEnum.formDatePicker,
    sortable: true,
    label: 'Remind at',
  },
  [SearchFormFieldEnum.FirstName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'First name',
  },
  [SearchFormFieldEnum.From]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'From',
  },
  [SearchFormFieldEnum.LastModifiedDate]: {
    searchFormFieldType: SearchFormInputEnum.formDatePicker,
    sortable: true,
    label: 'Last modified',
  },
  [SearchFormFieldEnum.LastName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Last name',
  },
  [SearchFormFieldEnum.Name]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Name',
  },
  [SearchFormFieldEnum.Priority]: {
    searchFormFieldType: SearchFormInputEnum.formMultipleSelect,
    sortable: true,
    label: 'Priority',
    toPayloadValueFormatter: (value: string[]): string[] => {
      return value.flatMap((element) => getEnumKeyByEnumValue(PriorityEnum, element) || []);
    },
    toFormValueFormatter: (value: string[]) =>
      value.map((element) => PriorityEnum[element as keyof typeof PriorityEnum]),
  },
  [SearchFormFieldEnum.ProjectActive]: {
    searchFormFieldType: SearchFormInputEnum.formCheckbox,
    sortable: false,
    label: 'Active',
  },
  [SearchFormFieldEnum.ProjectBackOfficesId]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Backoffice',
  },
  [SearchFormFieldEnum.ProjectBackOfficesFirstName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Project backoffices first name',
  },
  [SearchFormFieldEnum.ProjectBackOfficesLastName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Project backoffices last name',
  },
  [SearchFormFieldEnum.ProjectClientId]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Project client name',
  },
  [SearchFormFieldEnum.ProjectClientName]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Project client name',
  },
  [SearchFormFieldEnum.ProjectCoordinatorsId]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Project coordinator',
  },
  [SearchFormFieldEnum.ProjectCoordinatorsFirstName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Project coordinators first name',
  },
  [SearchFormFieldEnum.ProjectCoordinatorsLastName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Project coordinators last name',
  },
  [SearchFormFieldEnum.ProjectInterviewersId]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Project interviewer',
  },
  [SearchFormFieldEnum.ProjectInterviewersFirstName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Project interviewers first name',
  },
  [SearchFormFieldEnum.ProjectInterviewersLastName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Project interviewers last name',
  },
  [SearchFormFieldEnum.ProjectName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Project name',
  },
  [SearchFormFieldEnum.ProjectPriority]: {
    searchFormFieldType: SearchFormInputEnum.formCheckboxGroup,
    sortable: false,
    label: 'Priority',
    toPayloadValueFormatter: (value: string[]): string[] => {
      return value.flatMap((element) => getEnumKeyByEnumValue(PriorityEnum, element) || []);
    },
    toFormValueFormatter: (value: string[]) =>
      value.map((element) => PriorityEnum[element as keyof typeof PriorityEnum]),
  },
  [SearchFormFieldEnum.ProjectRecruitersId]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Project recruiter',
  },
  [SearchFormFieldEnum.ProjectRecruitersFirstName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Project recruiters first name',
  },
  [SearchFormFieldEnum.ProjectRecruitersLastName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Project recruiters last name',
  },
  [SearchFormFieldEnum.ProjectUUID]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Project',
  },
  [SearchFormFieldEnum.RecommendedBy]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Recommended by',
  },
  [SearchFormFieldEnum.Recruiters]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Recruiters',
  },
  [SearchFormFieldEnum.ReminderFor]: {
    searchFormFieldType: SearchFormInputEnum.formMultipleSelect,
    sortable: false,
    label: 'Supported contract',
    toPayloadValueFormatter: (value: string[]): string[] => {
      return value?.length
        ? value.flatMap((element) => getEnumKeyByEnumValue(ReminderObjectClassEnum, element) || [])
        : [];
    },
    toFormValueFormatter: (value: string[]) =>
      value.map((element) => ReminderObjectClassEnum[element as keyof typeof ReminderObjectClassEnum]),
  },
  [SearchFormFieldEnum.ReminderType]: {
    searchFormFieldType: SearchFormInputEnum.formMultipleSelect,
    sortable: false,
    label: 'Supported contract',
    toPayloadValueFormatter: (value: string[]): string[] => {
      return value?.length ? value.flatMap((element) => getEnumKeyByEnumValue(ReminderTypeEnum, element) || []) : [];
    },
    toFormValueFormatter: (value: string[]) =>
      value.map((element) => ReminderTypeEnum[element as keyof typeof ReminderTypeEnum]),
  },
  [SearchFormFieldEnum.RemotePreference]: {
    searchFormFieldType: SearchFormInputEnum.formMultipleSelect,
    sortable: false,
    label: 'Remote preference',
    toPayloadValueFormatter: (value: string[]): string[] => {
      return value.flatMap((element) => getEnumKeyByEnumValue(RemotePreferenceEnum, element) || []);
    },
    toFormValueFormatter: (value: string[]) =>
      value.map((element) => RemotePreferenceEnum[element as keyof typeof RemotePreferenceEnum]),
  },
  [SearchFormFieldEnum.ResponsibleId]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'Responsible',
  },
  [SearchFormFieldEnum.ResponsibleFirstName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Project responsibles first name',
  },
  [SearchFormFieldEnum.ResponsibleLastName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Project responsibles last name',
  },
  [SearchFormFieldEnum.SentDate]: {
    searchFormFieldType: SearchFormInputEnum.formDatePicker,
    sortable: true,
    label: 'Sent',
  },
  [SearchFormFieldEnum.Source]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Sent',
  },
  [SearchFormFieldEnum.Subject]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Subject',
  },
  [SearchFormFieldEnum.StatusActionRequired]: {
    searchFormFieldType: SearchFormInputEnum.formCheckbox,
    sortable: false,
    label: 'Action required',
  },
  [SearchFormFieldEnum.TechnicalInterviewAssessment]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: false,
    label: 'Technical score / assessment',
  },
  [SearchFormFieldEnum.TechnicalInterviewDate]: {
    searchFormFieldType: SearchFormInputEnum.formDatePicker,
    sortable: false,
    label: 'Technical interview date',
  },
  [SearchFormFieldEnum.TechnicalInterviewSummary]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: false,
    label: 'Technical interview description',
  },
  [SearchFormFieldEnum.Title]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'Title',
  },
  [SearchFormFieldEnum.TotalEmploymentCost]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: false,
    label: 'Total employment cost',
  },
  [SearchFormFieldEnum.UserFirstName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'User first name',
  },
  [SearchFormFieldEnum.UserLastName]: {
    searchFormFieldType: SearchFormInputEnum.formText,
    sortable: true,
    label: 'User last name',
  },
  [SearchFormFieldEnum.UUID]: {
    searchFormFieldType: SearchFormInputEnum.formSelect,
    sortable: true,
    label: 'UUID',
  },
  [SearchFormFieldEnum.PerfectMatch]: {
    searchFormFieldType: SearchFormInputEnum.formCheckbox,
    sortable: true,
    label: 'Perfect match',
  },
};
